import { computed, getCurrentInstance } from 'vue';
import { AuthState } from './types';

export function useAuthStore() {
    const vm = getCurrentInstance();

    const state = computed(() => vm.proxy.$store.state.auth as AuthState);

    const notice = computed(() => state.value.notice);
    const processing = computed(() => state.value.processing);
    const error = computed(() => state.value.error);
    const errorCode = computed(() => state.value.errorCode);
    const errorMsg = computed(() => state.value.errorMsg);
    const isUpdated = computed(() => state.value.isUpdated);
    const embedded = computed(() => state.value.embedded);
    const softRegistered = computed(() => state.value.softRegistered);

    const amIAuthed = computed(() => vm.proxy.$store.getters['auth/amIAuthed']() as boolean);
    const id = computed(() => vm.proxy.$store.getters['auth/id']() as number);
    const uid = computed(() => vm.proxy.$store.getters['auth/uid']() as string);
    const name = computed(() => vm.proxy.$store.getters['auth/name']() as string);
    const authorities = computed(() => vm.proxy.$store.getters['auth/authorities']() as Array<string>);
    const isAfterLogin = computed(() => vm.proxy.$store.getters['auth/afterLogin']() as boolean);

    /**
     * ローカルユーザーログイン
     * @param payload ログイン情報
     */
    async function login(payload: { uid: string; password: string; redirect?: string }) {
        await vm.proxy.$store.dispatch('auth/login', payload);
    }

    /**
     * ドメインユーザーログイン
     * @param payload ログイン情報
     */
    async function ldapLogin(payload: { uid: string; password: string; redirect?: string }) {
        await vm.proxy.$store.dispatch('auth/ldapLogin', payload);
    }

    /**
     * アプリケーションエラーを消す
     */
    async function dealError() {
        await vm.proxy.$store.dispatch('auth/dealError');
    }

    /**
     * 認証情報に書き換えがあったことを示すフラグをfalseにする
     */
    async function dealUpdate() {
        await vm.proxy.$store.dispatch('auth/dealUpdate');
    }

    /**
     * 表示中の通知情報を消す（確認ボタンや×ボタン押した際など）
     */
    async function dealNotice() {
        await vm.proxy.$store.dispatch('auth/dealNotice');
    }

    /**
     * SAMLユーザーログイン
     */
    async function sso() {
        await vm.proxy.$store.dispatch('auth/sso');
    }

    /**
     * ログアウト
     */
    async function logout() {
        await vm.proxy.$store.dispatch('auth/logout');
    }

    async function multifactorLogin(payload: {
        uid: string;
        password: string;
        onetimePassword: string;
        redirect?: string;
    }) {
        await vm.proxy.$store.dispatch('auth/multifactorLogin', payload);
    }

    async function ldapMultifactorLogin(payload: {
        uid: string;
        password: string;
        onetimePassword: string;
        redirect?: string;
    }) {
        await vm.proxy.$store.dispatch('auth/ldapMultifactorLogin', payload);
    }

    async function hardDeviceRegister(payload: { uid: string; password: string; serial_number: string }) {
        await vm.proxy.$store.dispatch('auth/hardDeviceRegister', payload);
    }

    async function sendUnlockMail(email: string) {
        await vm.proxy.$store.dispatch('auth/sendUnlockMail', email);
    }

    async function sendPasswordResetMail(email: string) {
        await vm.proxy.$store.dispatch('auth/sendPasswordResetMail', email);
    }

    async function sendUnconfirmedMail(email: string) {
        await vm.proxy.$store.dispatch('auth/sendUnconfirmedMail', email);
    }

    return {
        notice,
        processing,
        error,
        errorCode,
        errorMsg,
        isUpdated,
        isAfterLogin,
        embedded,
        softRegistered,
        amIAuthed,
        id,
        uid,
        name,
        authorities,
        login,
        ldapLogin,
        dealError,
        dealUpdate,
        dealNotice,
        sso,
        logout,
        multifactorLogin,
        ldapMultifactorLogin,
        hardDeviceRegister,
        sendUnlockMail,
        sendPasswordResetMail,
        sendUnconfirmedMail,
    };
}
